//* 지망생 정보 팝업
// 2025.08.20
// 희문
//* 지망생 정보를 직접 받아 출력하는 팝업
import React, { useEffect, useState } from "react";
import { getCurrentLangName, getFanPageUrl, getFlagByCountryCode, getStudentNickname } from "helpers/String.helper";
import LoadingComponent from "commons/loading/Loading.component";
import { getImageUrl } from "helpers/Image.helper";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { useNavigate } from "react-router-dom";
import { getStudentInfo } from "api/student/Popup.api";
import { addFollow, cancelFollow } from "api/student/Follow.api";

interface StudentInfoPopupProps {
    studentId: string;
    onClose: () => void;
}

const StudentInfoPopup: React.FC<StudentInfoPopupProps> = ({ studentId, onClose }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [info, setInfo] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isOnNetworking, setIsOnNetworking] = useState(false);

    const getStudentData = async () => {
        console.log("getStudentData", studentId);
        if (!studentId) return;

        try {
            setIsLoading(true);
            const data = await getStudentInfo(parseInt(studentId));
            if (data?.info) {
                setInfo(data.info);
            } else {
                setErrorMessage("지망생 정보를 찾을 수 없습니다.");
            }
        } catch (err: any) {
            console.error("학생 정보를 불러오는데 실패했습니다:", err);
            setErrorMessage(err.message || "지망생 정보를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getStudentData();
    }, [studentId]);

    const onFollow = async (studentId: number) => {
        console.log("onFollow", studentId);
        if (isOnNetworking) return;
        try {
            setIsOnNetworking(true);
            const data = await addFollow(studentId);
            if (data) {
                setInfo((prev: any) => prev ? { ...prev, isFollowing: true } : null);
            }
        } catch (err: any) {
            console.error("팔로우 처리에 실패했습니다:", err);
            alert(err.message || "팔로우 처리에 실패했습니다.");
        } finally {
            setIsOnNetworking(false);
        }
    };

    const onUnfollow = async (studentId: number) => {
        console.log("onUnfollow", studentId);
        if (isOnNetworking) return;
        try {
            setIsOnNetworking(true);
            const data = await cancelFollow(studentId);
            if (data) {
                setInfo((prev: any) => prev ? { ...prev, isFollowing: false } : null);
            }
        } catch (err: any) {
            console.error("언팔로우 처리에 실패했습니다:", err);
            alert(err.message || "언팔로우 처리에 실패했습니다.");
        } finally {
            setIsOnNetworking(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4 !m-0">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtTitle") || "지망생 정보"}
                    </h3>
                    <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {isLoading ? (
                    <LoadingComponent />
                ) : errorMessage ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                        <p className="text-center">{errorMessage}</p>
                    </div>
                ) : info ? (
                    <div className="flex flex-col items-center">
                        <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden mb-4">
                            <img
                                src={getImageUrl(info?.profileImageInfo)}
                                alt={info?.nickname}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <h2 className="mb-2 text-center">
                            <span className="text-xl font-bold">
                                {getStudentNickname(info)}
                            </span>
                            <br />
                            <span className="text-sm text-gray-600"> {info?.birthYear ? `(${info?.birthYear})` : ""}</span>
                        </h2>

                        <p className="text-gray-600 mb-4 text-center">
                            {getFlagByCountryCode(info?.countryInfo?.code)}
                            {getCurrentLangName(info?.countryInfo?.names)}
                        </p>

                        {info?.isActiveOfPublicProfile &&
                            <div className="flex flex-col gap-4 mb-8 w-full">
                                <div className="flex justify-center gap-8">
                                    <div className="text-center">
                                        <div className="text-gray-600 text-sm">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollowing") || "팔로잉"} / {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollower") || "팔로워"}</div>
                                        <div className="font-bold">{info?.followingCount || 0} / {info?.followerCount || 0}</div>

                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-sm">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFavoriteByAgency") || "기획사 즐겨찾기"}</div>
                                        <div className="font-bold">{info?.favoriteByAgencyCount || 0}</div>

                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-sm">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtTotalChastingChat") || "총 캐스팅 채팅"}</div>
                                        <div className="font-bold">{info?.totalCastingChatCount || 0}</div>

                                    </div>
                                </div>
                            </div>
                        }

                        <div className="flex flex-row gap-4 w-full">
                            {info && !info.isMine && (
                                <>
                                    {info?.isActiveOfPublicProfile &&
                                        <button
                                            className="px-4 py-2 rounded-lg w-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            onClick={() => {
                                                onClose();
                                                navigate(`/student/wannabe/${info.id}`);
                                            }}
                                        >
                                            {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtShowDetail") || "상세정보 보기"}
                                        </button>
                                    }
                                    <button
                                        className={`px-4 py-2 rounded-lg text-sm font-medium w-full
                                        ${info.isFollowing
                                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                : 'bg-[#CF3688] text-white hover:bg-[#CF3688]/90'}`}
                                        onClick={() => info.isFollowing ? onUnfollow(info.id) : onFollow(info.id)}
                                        disabled={isOnNetworking}
                                    >
                                        {!info.isFollowing && info.isFollower
                                            ? getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollowBack") || "맞팔로우"
                                            : info.isFollowing
                                                ? getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtUnfollow") || "언팔로우"
                                                : getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollow") || "팔로우"}
                                    </button>
                                </>
                            )}
                        </div>

                        {/* <div className="text-center">
                            <a
                                href={getFanPageUrl(info)}
                                className="!text-[#CF3688] hover:underline text-sm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFanPageVisit") || "팬페이지 방문"}
                            </a>
                        </div> */}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                        <p className="text-center">지망생 정보를 불러올 수 없습니다.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentInfoPopup;
